import React, { useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  useGetProjectAvailableCountriesQuery,
  useLazyGetProjectLocationsDefaultSettingsQuery,
} from '../../../../app/api'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { selectProjectLocations, updateProjectLocations } from '../../model/projectsSlice'
import { useTranslation } from 'react-i18next'
import { IAddCountriesForm } from '../../model/types/AddCountriesForm.interface'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import styles from './ProjectAddCountryModal.module.scss'

export const ProjectAddCountryModal = ({ id }: { id?: string }) => {
  const { t } = useTranslation()
  const locationsList = useAppSelector(selectProjectLocations)
  const dispatch = useAppDispatch()

  const { control, handleSubmit } = useForm<IAddCountriesForm>({
    mode: 'onChange',
    defaultValues: {},
  })

  const countriesValue = useWatch({ control, name: 'countries' })

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetProjectAvailableCountriesQuery(
    { projectId: Number(id) },
    {
      skip: !id,
    },
  )

  const filteredCountries = useMemo<IOption[]>(() => {
    const currentLocations = locationsList?.map((location) => String(location.country_id)) || []

    return (countries?.data?.items || [])
      .map((country) => ({ text: country.name, value: String(country.id) }))
      .filter((country) => !currentLocations.includes(country.value))
  }, [countries, locationsList])

  const [getLocationsDefaultSettings] = useLazyGetProjectLocationsDefaultSettingsQuery()

  if (countriesLoading || countriesFetching) {
    return <Preloader />
  }

  const submit = (value: IAddCountriesForm) => {
    getLocationsDefaultSettings({ countryIds: value.countries.map(Number) }).then(({ data }) => {
      dispatch(updateProjectLocations(data?.data?.items ?? []))
      dispatch(hideModal())
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(submit)}>
      <h3 className={styles.title}>{t('pages.project.addCountries')}</h3>
      <div className={styles.body}>
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => (
            <BpSelect
              dropPosition={'auto'}
              {...fieldProps}
              multiple
              required
              disabled={!filteredCountries?.length}
              label={t('fields.countries')}
              loading={countriesLoading || countriesFetching}
              options={filteredCountries}
              placeholder={t('fields.selectCountries')}
              onChange={(e) => {
                const value = [].slice.call(e.target.selectedOptions).map((o: IOption) => o.value)

                onChange(value)
              }}
            />
          )}
          control={control}
          name='countries'
        />
      </div>
      <div className={styles.footer}>
        <BpButton className={styles.addButton} disabled={!countriesValue?.length} type='submit'>
          {t('buttons.add')}
        </BpButton>
        <BpButton theme='transparent' type='button' onClick={() => dispatch(hideModal())}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </form>
  )
}
